import React, { useEffect, useState } from 'react';
import { Plot, PlotStatus, UpdatePlotRequest } from '../types';
import apiService from '../services/api';
import {
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface Props {
  plot: Plot;
  statuses: PlotStatus[];
  onClose: () => void;
  onUpdated: (updatedPlot: Plot) => void;
}

const StatusUpdateModal: React.FC<Props> = ({ plot, statuses, onClose, onUpdated }) => {
  const [selectedStatusId, setSelectedStatusId] = useState<number>(plot.status_id);
  const [formData, setFormData] = useState<UpdatePlotRequest>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    // Reset form when status changes
    setFormData({});
    setErrors({});
  }, [selectedStatusId]);

  const requiredFieldsForStatus = (id: number): string[] => {
    switch (id) {
      case 4: // Token
        return ['customer_name', 'customer_phone', 'token_amount'];
      case 5: // Agreement
        return ['customer_name', 'customer_phone', 'agreement_date', 'paid_amount'];
      case 6: // Registered
        return ['customer_name', 'customer_phone', 'registration_date', 'paid_amount'];
      default:
        return [];
    }
  };

  const validate = (): boolean => {
    const req = requiredFieldsForStatus(selectedStatusId);
    const newErrors: { [key: string]: string } = {};
    req.forEach((field) => {
      if (!formData[field as keyof UpdatePlotRequest]) {
        newErrors[field] = 'Required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof UpdatePlotRequest, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setSubmitting(true);
      setApiError(null);
      const payload: UpdatePlotRequest = {
        ...formData,
        status_id: selectedStatusId
      };
      const resp = await apiService.updatePlot(plot.id, payload);
      onUpdated(resp.data.plot);
      onClose();
    } catch (err: any) {
      setApiError(err.message || 'Failed to update plot');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: string) => {
    switch (field) {
      case 'customer_name':
        return (
          <div>
            <label className="form-label">Customer Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.customer_name || ''}
              onChange={(e) => handleChange('customer_name', e.target.value)}
            />
            {errors.customer_name && <p className="text-xs text-red-600 mt-1">{errors.customer_name}</p>}
          </div>
        );
      case 'customer_phone':
        return (
          <div>
            <label className="form-label">Customer Phone *</label>
            <input
              type="tel"
              className="form-input"
              value={formData.customer_phone || ''}
              onChange={(e) => handleChange('customer_phone', e.target.value)}
            />
            {errors.customer_phone && <p className="text-xs text-red-600 mt-1">{errors.customer_phone}</p>}
          </div>
        );
      case 'token_amount':
        return (
          <div>
            <label className="form-label">Token Amount (₹) *</label>
            <input
              type="number"
              min={0}
              className="form-input"
              value={formData.token_amount ?? ''}
              onChange={(e) => handleChange('token_amount', parseFloat(e.target.value))}
            />
            {errors.token_amount && <p className="text-xs text-red-600 mt-1">{errors.token_amount}</p>}
          </div>
        );
      case 'agreement_date':
        return (
          <div>
            <label className="form-label">Agreement Date *</label>
            <input
              type="date"
              className="form-input"
              value={formData.agreement_date || ''}
              onChange={(e) => handleChange('agreement_date', e.target.value)}
            />
            {errors.agreement_date && <p className="text-xs text-red-600 mt-1">{errors.agreement_date}</p>}
          </div>
        );
      case 'registration_date':
        return (
          <div>
            <label className="form-label">Registration Date *</label>
            <input
              type="date"
              className="form-input"
              value={formData.registration_date || ''}
              onChange={(e) => handleChange('registration_date', e.target.value)}
            />
            {errors.registration_date && <p className="text-xs text-red-600 mt-1">{errors.registration_date}</p>}
          </div>
        );
      case 'paid_amount':
        return (
          <div>
            <label className="form-label">Paid Amount (₹) *</label>
            <input
              type="number"
              min={0}
              className="form-input"
              value={formData.paid_amount ?? ''}
              onChange={(e) => handleChange('paid_amount', parseFloat(e.target.value))}
            />
            {errors.paid_amount && <p className="text-xs text-red-600 mt-1">{errors.paid_amount}</p>}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-700 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Update Plot Status</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="form-label">Status</label>
            <select
              className="form-input"
              value={selectedStatusId}
              onChange={(e) => setSelectedStatusId(parseInt(e.target.value))}
            >
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>{s.status_name}</option>
              ))}
            </select>
          </div>

          {/* Dynamic required fields */}
          {requiredFieldsForStatus(selectedStatusId).map((field) => (
            <React.Fragment key={field}>{renderField(field)}</React.Fragment>
          ))}

          {apiError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start space-x-2">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
              <span>{apiError}</span>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t flex justify-end space-x-3">
          <button onClick={onClose} className="btn-outline">Cancel</button>
          <button
            onClick={handleSubmit}
            className="btn-primary inline-flex items-center"
            disabled={submitting}
          >
            {submitting ? 'Updating...' : (<><CheckIcon className="h-4 w-4 mr-1" /> Update</>)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusUpdateModal; 